import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';

export type Locale = 'zh-TW' | 'en';

type TranslationKey =
  | 'app.subtitle'
  | 'auth.google'
  | 'auth.logout'
  | 'auth.googleTitle'
  | 'auth.logoutTitle'
  | 'language.toggle'
  | 'language.name'
  | 'tabs.assessment'
  | 'tabs.aiHelper'
  | 'tabs.aiHelperTooltip'
  | 'tabs.favorites'
  | 'tabs.history'
  | 'tabs.voices'
  | 'favorites.sentences'
  | 'favorites.tags'
  | 'favorites.shareBackup'
  | 'favorites.searchPlaceholder'
  | 'favorites.search'
  | 'favorites.clear'
  | 'favorites.all'
  | 'favorites.clearAll'
  | 'tags.defaultExample'
  | 'voice.title'
  | 'voice.tooltip'
  | 'voice.speed'
  | 'voice.genderFemale'
  | 'voice.genderMale'
  | 'voice.description'
  | 'history.empty'
  | 'history.clear'
  | 'history.delete'
  | 'history.accuracy'
  | 'history.fluency'
  | 'history.completeness'
  | 'history.pronunciation'
  | 'history.showWords'
  | 'history.hideWords'
  | 'assessment.score'
  | 'assessment.stopRecording'
  | 'assessment.processing'
  | 'assessment.playAiVoice'
  | 'assessment.recording'
  | 'assessment.streaming'
  | 'assessment.cacheHit'
  | 'assessment.totalScore'
  | 'assessment.sentenceAnalysis'
  | 'assessment.noWordScores'
  | 'assessment.recognizedText'
  | 'assessment.result'
  | 'footer.contact'
  | 'footer.beta'
  | 'footer.privacy'
  | 'tags.emptyFavorites'
  | 'tags.edit'
  | 'tags.addNew'
  | 'tags.namePlaceholder'
  | 'tags.nameRequired'
  | 'tags.update'
  | 'tags.add'
  | 'tags.cancel'
  | 'tags.existing'
  | 'tags.deleteConfirm'
  | 'ai.placeholder'
  | 'ai.addImage'
  | 'ai.favoritePrompt'
  | 'ai.removePromptFavorite'
  | 'ai.historyEnable'
  | 'ai.historyDisable'
  | 'ai.historyEnabledTip'
  | 'ai.historyDisabledTip'
  | 'ai.processing'
  | 'ai.compressing'
  | 'ai.generating'
  | 'ai.tooltip'
  | 'ai.dropImage'
  | 'ai.favoritePrompts'
  | 'ai.noFavoritePrompts'
  | 'ai.examplePrompts'
  | 'ai.response'
  | 'ai.noResponse'
  | 'ai.example.image'
  | 'ai.example.senior'
  | 'ai.example.custom'
  | 'ai.example.bike'
  | 'login.requiredTitle'
  | 'login.defaultAction'
  | 'login.defaultMessage'
  | 'login.afterLogin'
  | 'login.benefitFavorites'
  | 'login.benefitTags'
  | 'login.benefitShareHistory'
  | 'login.benefitSync'
  | 'login.skip'
  | 'login.google';

const STORAGE_KEY = 'nicetone_locale';

const translations: Record<Locale, Record<TranslationKey, string>> = {
  'zh-TW': {
    'app.subtitle': '口袋裡的AI英語教練',
    'auth.google': 'Google',
    'auth.logout': '登出',
    'auth.googleTitle': '使用 Google 登入',
    'auth.logoutTitle': '登出',
    'language.toggle': '切換語言',
    'language.name': '中文',
    'tabs.assessment': '發音評分',
    'tabs.aiHelper': 'AI造句幫手',
    'tabs.aiHelperTooltip': '在這裡輸入指令，要求AI造出能幫助你英文能力的句子。例如：請造一些關於旅遊的基礎句子，或者幫我練習過去式動詞的句子。',
    'tabs.favorites': '我的最愛',
    'tabs.history': '發音歷史',
    'tabs.voices': '選擇語音',
    'favorites.sentences': '📝 句子',
    'favorites.tags': '🏷️ 標籤',
    'favorites.shareBackup': '🔗 分享與備份',
    'favorites.searchPlaceholder': '搜尋收藏的句子...',
    'favorites.search': '搜尋',
    'favorites.clear': '清除',
    'favorites.all': '全部',
    'favorites.clearAll': '清空所有句子',
    'tags.defaultExample': '範例標籤',
    'voice.title': 'AI語音設置',
    'voice.tooltip': '使用AI合成語音進行朗讀',
    'voice.speed': '語音速度：{{rate}}x',
    'voice.genderFemale': '女性',
    'voice.genderMale': '男性',
    'voice.description': '{{name}} {{gender}}聲音',
    'history.empty': '暫無歷史紀錄，錄製完畢後會自動出現紀錄',
    'history.clear': '清空歷史',
    'history.delete': '刪除',
    'history.accuracy': '準確性',
    'history.fluency': '流暢性',
    'history.completeness': '完整性',
    'history.pronunciation': '發音',
    'history.showWords': '顯示單詞評分',
    'history.hideWords': '隱藏單詞評分',
    'assessment.score': '評分',
    'assessment.stopRecording': '停止錄音',
    'assessment.processing': '處理中...',
    'assessment.playAiVoice': '使用AI語音播放',
    'assessment.recording': '錄音中... (最長30秒)',
    'assessment.streaming': '流式處理中...',
    'assessment.cacheHit': '使用已緩存的語音',
    'assessment.totalScore': '總分',
    'assessment.sentenceAnalysis': '句子分析',
    'assessment.noWordScores': '無法獲取詳細單詞評分數據',
    'assessment.recognizedText': '識別文本',
    'assessment.result': '評分結果',
    'footer.contact': '合作聯絡：',
    'footer.beta': 'Beta版測試中，不保證資料穩定儲存',
    'footer.privacy': '隱私政策',
    'tags.emptyFavorites': '還沒有收藏的句子，請使用文本輸入框右下方的星號按鈕(★)新增',
    'tags.edit': '編輯標籤',
    'tags.addNew': '新增新標籤',
    'tags.namePlaceholder': '標籤名稱...',
    'tags.nameRequired': '請輸入標籤名稱',
    'tags.update': '更新',
    'tags.add': '新增',
    'tags.cancel': '取消',
    'tags.existing': '現有標籤',
    'tags.deleteConfirm': '確定要刪除標籤 "{{name}}" 嗎？',
    'ai.placeholder': "請描述你希望AI幫你創造的句子，或參考下方範例提示",
    'ai.addImage': '新增圖片',
    'ai.favoritePrompt': '收藏指令',
    'ai.removePromptFavorite': '刪除指令收藏',
    'ai.historyEnable': '開啟發音歷史記錄發送',
    'ai.historyDisable': '關閉發音歷史記錄發送',
    'ai.historyEnabledTip': '已開啟發音歷史記錄發送',
    'ai.historyDisabledTip': '已關閉發音歷史記錄發送',
    'ai.processing': '處理中...',
    'ai.compressing': '圖片壓縮中...',
    'ai.generating': 'AI造句',
    'ai.tooltip': '想要造句麼? 除了可以輸入您的造句指令之外，您也可以上傳相關圖片供AI分析。{{historyStatus}}點擊上方麥克風按鈕可以切換此功能。',
    'ai.dropImage': '將圖片拖曳到此或點擊上方按鈕上傳',
    'ai.favoritePrompts': '我的最愛指令：',
    'ai.noFavoritePrompts': '還沒有收藏的指令',
    'ai.examplePrompts': '範例提示：',
    'ai.response': 'AI 回應',
    'ai.noResponse': '無回應訊息',
    'ai.example.image': '針對圖片產生3個跟圖片內容有關的句子',
    'ai.example.senior': '幫我產生5個高中三年級英文考100的人會寫出的短文(不要只有一句話)',
    'ai.example.custom': '客製化發音糾正：分析我的發音歷史記錄，幫我用這些字做一個短文(需開啟傳送發音歷史記錄按鈕，在右上角麥克風圖案)',
    'ai.example.bike': '幫我創造在腳踏車店，會講到的5個來回對話句子',
    'login.requiredTitle': '需要登入',
    'login.defaultAction': '此功能',
    'login.defaultMessage': '{{actionName}}需要登入才能將您的設定儲存到雲端，確保在不同裝置間同步。',
    'login.afterLogin': '登入後您可以：',
    'login.benefitFavorites': '在雲端儲存您的收藏',
    'login.benefitTags': '同步自訂標籤',
    'login.benefitShareHistory': '儲存分享記錄',
    'login.benefitSync': '在不同裝置間同步資料',
    'login.skip': '暫不登入',
    'login.google': '使用 Google 登入'
  },
  en: {
    'app.subtitle': 'Your pocket AI English coach',
    'auth.google': 'Google',
    'auth.logout': 'Sign out',
    'auth.googleTitle': 'Sign in with Google',
    'auth.logoutTitle': 'Sign out',
    'language.toggle': 'Switch language',
    'language.name': 'English',
    'tabs.assessment': 'Pronunciation Score',
    'tabs.aiHelper': 'AI Sentence Helper',
    'tabs.aiHelperTooltip': 'Enter an instruction here and ask AI to create English practice sentences. For example: create beginner travel sentences, or help me practice past-tense verbs.',
    'tabs.favorites': 'Favorites',
    'tabs.history': 'Pronunciation History',
    'tabs.voices': 'Voice Selection',
    'favorites.sentences': '📝 Sentences',
    'favorites.tags': '🏷️ Tags',
    'favorites.shareBackup': '🔗 Share & Backup',
    'favorites.searchPlaceholder': 'Search favorite sentences...',
    'favorites.search': 'Search',
    'favorites.clear': 'Clear',
    'favorites.all': 'All',
    'favorites.clearAll': 'Clear All Sentences',
    'tags.defaultExample': 'Example Tag',
    'voice.title': 'AI Voice Settings',
    'voice.tooltip': 'Use AI-generated speech for read-aloud practice',
    'voice.speed': 'Speech speed: {{rate}}x',
    'voice.genderFemale': 'Female',
    'voice.genderMale': 'Male',
    'voice.description': '{{name}} {{gender}} voice',
    'history.empty': 'No pronunciation history yet. Records will appear after you finish recording.',
    'history.clear': 'Clear History',
    'history.delete': 'Delete',
    'history.accuracy': 'Accuracy',
    'history.fluency': 'Fluency',
    'history.completeness': 'Completeness',
    'history.pronunciation': 'Pronunciation',
    'history.showWords': 'Show Word Scores',
    'history.hideWords': 'Hide Word Scores',
    'assessment.score': 'Score',
    'assessment.stopRecording': 'Stop Recording',
    'assessment.processing': 'Processing...',
    'assessment.playAiVoice': 'Play with AI voice',
    'assessment.recording': 'Recording... (30 seconds max)',
    'assessment.streaming': 'Streaming...',
    'assessment.cacheHit': 'Using cached voice',
    'assessment.totalScore': 'Total Score',
    'assessment.sentenceAnalysis': 'Sentence Analysis',
    'assessment.noWordScores': 'Detailed word scores are unavailable.',
    'assessment.recognizedText': 'Recognized Text',
    'assessment.result': 'Score Result',
    'footer.contact': 'Contact:',
    'footer.beta': 'Beta testing. Data stability is not guaranteed.',
    'footer.privacy': 'Privacy Policy',
    'tags.emptyFavorites': 'No favorite sentences yet. Use the star button (★) near the text input to add one.',
    'tags.edit': 'Edit Tag',
    'tags.addNew': 'Add New Tag',
    'tags.namePlaceholder': 'Tag name...',
    'tags.nameRequired': 'Please enter a tag name',
    'tags.update': 'Update',
    'tags.add': 'Add',
    'tags.cancel': 'Cancel',
    'tags.existing': 'Existing Tags',
    'tags.deleteConfirm': 'Delete tag "{{name}}"?',
    'ai.placeholder': 'Describe the sentences you want AI to create, or use one of the examples below.',
    'ai.addImage': 'Add image',
    'ai.favoritePrompt': 'Save prompt',
    'ai.removePromptFavorite': 'Remove saved prompt',
    'ai.historyEnable': 'Send pronunciation history',
    'ai.historyDisable': 'Stop sending pronunciation history',
    'ai.historyEnabledTip': 'Pronunciation history sending is on.',
    'ai.historyDisabledTip': 'Pronunciation history sending is off.',
    'ai.processing': 'Processing...',
    'ai.compressing': 'Compressing image...',
    'ai.generating': 'Generate Sentences',
    'ai.tooltip': 'Want AI-made practice sentences? You can type an instruction and upload images for AI analysis. {{historyStatus}} Click the microphone button above to switch this feature.',
    'ai.dropImage': 'Drag images here or click the button above to upload',
    'ai.favoritePrompts': 'Favorite prompts:',
    'ai.noFavoritePrompts': 'No favorite prompts yet',
    'ai.examplePrompts': 'Example prompts:',
    'ai.response': 'AI Response',
    'ai.noResponse': 'No response message',
    'ai.example.image': 'Create 3 sentences related to the image content.',
    'ai.example.senior': 'Create 5 short passages written at a top senior high school English level. Do not make them single-sentence passages.',
    'ai.example.custom': 'Personalized pronunciation correction: analyze my pronunciation history and create a short passage using those words. The history toggle must be on.',
    'ai.example.bike': 'Create 5 back-and-forth dialogue sentences that could happen in a bicycle shop.',
    'login.requiredTitle': 'Sign In Required',
    'login.defaultAction': 'This feature',
    'login.defaultMessage': '{{actionName}} requires sign-in so your settings can be saved to the cloud and synced across devices.',
    'login.afterLogin': 'After signing in, you can:',
    'login.benefitFavorites': 'Save favorites in the cloud',
    'login.benefitTags': 'Sync custom tags',
    'login.benefitShareHistory': 'Save sharing history',
    'login.benefitSync': 'Sync data across devices',
    'login.skip': 'Not now',
    'login.google': 'Sign in with Google'
  }
};

interface I18nContextValue {
  locale: Locale;
  setLocale: (locale: Locale) => void;
  toggleLocale: () => void;
  t: (key: TranslationKey, params?: Record<string, string>) => string;
}

const I18nContext = createContext<I18nContextValue | null>(null);

function detectLocale(): Locale {
  if (typeof window === 'undefined') return 'en';

  const saved = window.localStorage.getItem(STORAGE_KEY) as Locale | null;
  if (saved === 'zh-TW' || saved === 'en') return saved;

  const browserLocale = window.navigator.language || '';
  if (browserLocale.toLowerCase().startsWith('zh')) return 'zh-TW';

  return 'en';
}

export const I18nProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [locale, setLocaleState] = useState<Locale>(() => detectLocale());

  useEffect(() => {
    document.documentElement.lang = locale;
    window.localStorage.setItem(STORAGE_KEY, locale);
  }, [locale]);

  const value = useMemo<I18nContextValue>(() => {
    const setLocale = (nextLocale: Locale) => setLocaleState(nextLocale);
    const toggleLocale = () => setLocaleState(current => current === 'zh-TW' ? 'en' : 'zh-TW');
    const t = (key: TranslationKey, params?: Record<string, string>) => {
      let text = translations[locale][key] || translations.en[key] || key;
      if (params) {
        Object.entries(params).forEach(([paramKey, paramValue]) => {
          text = text.replaceAll(`{{${paramKey}}}`, paramValue);
        });
      }
      return text;
    };

    return { locale, setLocale, toggleLocale, t };
  }, [locale]);

  return (
    <I18nContext.Provider value={value}>
      {children}
    </I18nContext.Provider>
  );
};

export function useI18n(): I18nContextValue {
  const context = useContext(I18nContext);
  if (!context) {
    throw new Error('useI18n must be used inside I18nProvider');
  }
  return context;
}
